/**
 * Convert standalone timer config into a WorkoutNode tree for device casting.
 *
 * The resulting tree uses the same shape as AI-generated plans so it can be
 * passed directly to pushConfigToDevice() → backend translateNode() → firmware.
 *
 * @param {string} mode  - Timer mode key from TIMER_PRESETS
 * @param {object} config - Timer settings (work, rest, rounds, sets, etc.)
 * @returns {object|null} WorkoutNode tree, or null if mode is not castable
 */

import { getTimerMacroLabels } from "../config/timers";

let _idCounter = 0;
const uid = () => `cast-${++_idCounter}`;

function makeGroup(label, repeats, children, extra = {}) {
  return {
    id: uid(),
    type: "Group",
    label,
    value: repeats,
    unit: "rounds",
    children,
    ...extra,
  };
}
function makeLeaf(type, label, seconds, autoStart = true) {
  return { id: uid(), type, label, value: seconds, unit: "s", autoStart };
}

export function buildStandaloneSessionPlan(mode, config) {
  _idCounter = 0;

  const sets = config.sets || 1;
  const setRest = config.setRest || 60;

  switch (mode) {
    case "tabata":
    case "hiit": {
      const { groupLabel, title } = getTimerMacroLabels(mode);
      const roundChildren = [
        makeLeaf("Work", "Work", config.work),
        makeLeaf("Rest", "Rest", config.rest),
      ];
      const setChildren = [makeGroup(groupLabel, config.rounds, roundChildren)];
      if (sets > 1) {
        setChildren.push(makeLeaf("SetRest", "Set Rest", setRest));
      }
      return makeGroup(title, 1, [makeGroup("Set", sets, setChildren)]);
    }

    case "emom": {
      const { groupLabel, title } = getTimerMacroLabels(mode);
      const setChildren = [
        makeGroup(groupLabel, config.rounds, [
          makeLeaf("Work", "EMOM", config.duration),
        ]),
      ];
      if (sets > 1) {
        setChildren.push(makeLeaf("SetRest", "Set Rest", setRest));
      }
      return makeGroup(title, 1, [makeGroup("Set", sets, setChildren)]);
    }

    case "amrap":
    case "countdown": {
      const label = mode === "amrap" ? "AMRAP" : "Countdown";
      const setChildren = [makeLeaf("Work", label, config.duration)];
      if (sets > 1) {
        setChildren.push(makeLeaf("SetRest", "Set Rest", setRest));
      }
      return makeGroup(label, 1, [makeGroup("Set", sets, setChildren)]);
    }

    case "circuit": {
      const exerciseChildren = [];
      const exerciseCount = config.exercises || 6;
      for (let i = 0; i < exerciseCount; i++) {
        exerciseChildren.push(
          makeLeaf("Work", `Exercise ${i + 1}`, config.exerciseTime),
        );
        if (i < exerciseCount - 1) {
          exerciseChildren.push(makeLeaf("Rest", "Rest", config.restTime));
        }
      }
      return makeGroup("Circuit", config.rounds || 3, exerciseChildren);
    }

    case "stopwatch":
      return makeGroup("Stopwatch", 1, [makeLeaf("Work", "Stopwatch", 0)], {
        countUpMode: true,
      });

    // Free training (#146): a stopwatch whose record is what you logged as
    // you went (#157), filed as the activity you pick at the end. See
    // utils/freeTraining.js and utils/freeTrainingLog.js.
    case "free":
      return makeGroup("Free training", 1, [makeLeaf("Work", "Free training", 0)], {
        countUpMode: true,
        freeTraining: true,
      });

    case "forTime":
      return makeGroup("For Time", 1, [makeLeaf("Work", "For Time", 0)], {
        countUpMode: true,
      });

    default:
      return null;
  }
}

export function getStandalonePhaseNodes(plan, mode) {
  if (!plan?.children?.length) return {};

  // Tabata/HIIT/EMOM/AMRAP/Countdown wrap their content inside a synthetic
  // "Set" group so that the root node's repeat count is honoured by
  // compileWorkoutPlan (which only walks plan.children, not plan.value).
  // Unwrap that level here so the outline still finds the inner nodes.
  const setGroup =
    plan.children[0]?.type === "Group" && plan.children[0]?.label === "Set"
      ? plan.children[0]
      : null;

  switch (mode) {
    case "tabata":
    case "hiit": {
      const setChildren = setGroup?.children || plan.children;
      const roundGroup = setChildren[0];
      return {
        work: roundGroup?.children?.[0] || null,
        rest: roundGroup?.children?.[1] || null,
        setRest: setChildren[1] || null,
      };
    }

    case "emom": {
      const setChildren = setGroup?.children || plan.children;
      const minuteGroup = setChildren[0];
      return {
        work: minuteGroup?.children?.[0] || null,
        setRest: setChildren[1] || null,
      };
    }

    case "countdown":
    case "amrap": {
      const setChildren = setGroup?.children || plan.children;
      return {
        work: setChildren[0] || null,
        setRest: setChildren[1] || null,
      };
    }

    case "stopwatch":
    case "free":
    case "forTime":
      return {
        work: plan.children[0] || null,
        setRest: plan.children[1] || null,
      };

    case "circuit": {
      const workNodes = [];
      const restNodes = [];
      for (const child of plan.children) {
        if (child?.type === "Work") workNodes.push(child);
        if (child?.type === "Rest" || child?.type === "Transition") restNodes.push(child);
      }
      return {
        work: workNodes[0] || null,
        rest: restNodes[0] || null,
      };
    }

    default:
      return {};
  }
}
